import { useMediaState, type MediaPlayerInstance } from '@vidstack/react'
import { useRef, useEffect, useMemo } from 'react'
import { tracking } from '@/tracking'

const MILESTONE_LABELS = [25, 50, 75, 100] as const

export interface VideoMilestoneTrackingConfig {
  /** Kissmetrics event name (e.g. 'Reached download video milestone'). */
  eventName: string
  /** Base properties merged with the auto-added `Milestone` prop on each fired event. */
  properties: Record<string, unknown>
}

interface VideoProgressTrackerProps {
  playerRef: React.RefObject<MediaPlayerInstance>
  fileId: number
  /** Owned by the playback session so source retries do not repeat milestones. */
  milestonesFired: Set<string>
  /**
   * Back-compat shortcut for the account downloads surface: when set, the tracker
   * emits `Reached download video milestone` with the download-style properties.
   * Prefer `milestoneTracking` for new surfaces.
   */
  productId?: number
  videoTitle?: string
  durationHint?: number | null
  /**
   * Generic milestone tracking config; takes precedence over `productId`.
   * Use for non-download surfaces (e.g. VI Studio) where the event name and
   * property labels need to differ.
   */
  milestoneTracking?: VideoMilestoneTrackingConfig
}

export const VideoProgressTracker = ({
  playerRef,
  fileId,
  productId,
  videoTitle,
  durationHint,
  milestoneTracking,
  milestonesFired,
}: VideoProgressTrackerProps) => {
  const currentTime = useMediaState('currentTime', playerRef)
  const mediaDuration = useMediaState('duration', playerRef)
  const ended = useMediaState('ended', playerRef)

  // Resolve the milestone config; explicit config wins over the productId back-compat path.
  const effectiveMilestoneConfig: VideoMilestoneTrackingConfig | null =
    useMemo(() => {
      if (milestoneTracking) return milestoneTracking
      if (productId) {
        return {
          eventName: 'Reached download video milestone',
          properties: {
            'Download name': videoTitle || '',
            'Download file ID': fileId,
            'Download product ID': productId,
          },
        }
      }
      return null
    }, [milestoneTracking, productId, videoTitle, fileId])

  // Read the current config from a ref inside the progress effect so changes to its
  // identity do not cause the milestone effect to re-run on every render.
  const milestoneConfigRef = useRef(effectiveMilestoneConfig)
  useEffect(() => {
    milestoneConfigRef.current = effectiveMilestoneConfig
  }, [effectiveMilestoneConfig])

  const effectiveDuration =
    durationHint && durationHint > 0
      ? durationHint
      : mediaDuration && mediaDuration > 0
        ? mediaDuration
        : 0

  useEffect(() => {
    const config = milestoneConfigRef.current
    if (!config) return
    if (!effectiveDuration || effectiveDuration <= 0) return
    if (!Number.isFinite(currentTime)) return

    const ratio = Math.min(1, Math.max(0, currentTime / effectiveDuration))
    const reachedEnd = ended || ratio >= 0.995

    for (const m of MILESTONE_LABELS) {
      const milestoneKey = `${fileId}:${config.eventName}:${m}`
      if (milestonesFired.has(milestoneKey)) continue
      const threshold = m / 100
      const crossed = m === 100 ? reachedEnd : ratio + 0.001 >= threshold
      if (crossed) {
        milestonesFired.add(milestoneKey)
        tracking.track(config.eventName, {
          ...config.properties,
          Milestone: `${m}%`,
        })
      }
    }
  }, [currentTime, effectiveDuration, ended, fileId, milestonesFired])

  return null
}
